import { ApolloError } from "@apollo/client";
import { FormikErrors } from "formik";
import {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { CustomQuestionAnswersByQuestionId } from "src/schemas/CustomQuestionType";
import { formatNestedQuestionAsProps } from "src/services/formTemplate/customQuestion";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { RemoteData } from "src/types/remoteData";
import { useGetPersonAnswerBankQuery } from "../api/answerBank";
import { AnswerBankRecord } from "../list/schemas/AnswerBank";
import { CloneIdsGroupedBySourceIds, mapCloneIdsBySourceIds } from "./helpers";

type CustomQuestionContextProps = {
  organization: GQL.Organization;
  customQuestion: AF.CustomQuestion<AF.WithId>;
  customQuestionType: GQL.GetCustomQuestionType_custom_question_type_by_pk;
  formId: uuid;
  personId: uuid;
  sourceIdCloneIdMapping: CloneIdsGroupedBySourceIds;
  setShowAnswerBank: (value: boolean) => void;
  setAnswerToEdit: (value: AnswerBankRecord | null) => void;
};

type CustomQuestionContextType = CustomQuestionContextProps & {
  fieldQuestions: AF.ClonedQuestion<AF.WithId>[];
  validate: (
    values: CustomQuestionAnswersByQuestionId
  ) => Promise<FormikErrors<CustomQuestionAnswersByQuestionId>>;
  answerBankRemoteData: RemoteData<ApolloError, AnswerBankRecord[]>;
};

const CustomQuestionContext = createContext<CustomQuestionContextType>({
  organization: {} as GQL.Organization,
  customQuestion: {} as AF.CustomQuestion<AF.WithId>,
  customQuestionType:
    {} as GQL.GetCustomQuestionType_custom_question_type_by_pk,
  formId: "",
  personId: "",
  sourceIdCloneIdMapping: {} as CloneIdsGroupedBySourceIds,
  setShowAnswerBank: () => {},
  setAnswerToEdit: () => {},
  fieldQuestions: [],
  validate: async () => {
    return {};
  },
  answerBankRemoteData: {} as RemoteData<ApolloError, AnswerBankRecord[]>,
});

export const CustomQuestionContextProvider: FunctionComponent<
  PropsWithChildren<Omit<CustomQuestionContextProps, "sourceIdCloneIdMapping">>
> = (props) => {
  const { children, ...contextProps } = props;

  const sourceIdCloneIdMapping = useMemo(() => {
    return mapCloneIdsBySourceIds(
      contextProps.customQuestionType,
      contextProps.customQuestion.nestedQuestions
    );
  }, [contextProps.customQuestionType, contextProps.customQuestion]);

  const fieldQuestions: AF.ClonedQuestion<AF.WithId>[] = useMemo(() => {
    return props.customQuestionType.custom_question_type_fields
      .map((field) => {
        return formatNestedQuestionAsProps(field.question);
      })
      .filter(isNotNull);
  }, [props.customQuestionType.custom_question_type_fields]);

  const validate = useCallback(
    (values: CustomQuestionAnswersByQuestionId) => {
      return validateFormTemplate(fieldQuestions, {
        rankedSchoolIds: [],
        previousFormSchoolIds: [],
      })(values);
    },
    [fieldQuestions]
  );

  const answerBankRemoteData = useGetPersonAnswerBankQuery(
    sourceIdCloneIdMapping,
    {
      custom_question_type_id: contextProps.customQuestionType.id,
      person_id: contextProps.personId,
    }
  );

  return (
    <CustomQuestionContext.Provider
      value={{
        ...contextProps,
        sourceIdCloneIdMapping,
        fieldQuestions,
        validate,
        answerBankRemoteData,
      }}
    >
      {children}
    </CustomQuestionContext.Provider>
  );
};

export function useCustomQuestionContext() {
  const context = useContext(CustomQuestionContext);
  if (context === undefined) {
    throw new Error(
      "useCustomQuestionContext must be used within a CustomQuestionContextProvider"
    );
  }

  return context;
}
